import { useState, useCallback, useEffect } from 'react';
import {
  addItemToManualServiceOrder,
  updateItemInManualServiceOrder,
  removeItemFromManualServiceOrder,
  getPrefilledCart,
  previewManualServiceOrderCashback
} from '../services/subscriberWalletService';
import { mapCartItemFromAPI, validateCartItems } from '../utils/medicineHelpers';

export const useMedicineCart = (currentRole, orderId, id, walletTransactionId, isModal, onDeliveryChargeChange) => {
  const [cart, setCart] = useState([]);
  const [cartCashback, setCartCashback] = useState(0);

  useEffect(() => {
    if (!orderId || !currentRole) return;
    const isIncomplete = (value) => value === null || value === undefined || value === '';
    const hasInvalidLine = cart.some(item =>
      !item.outOfStock && (item.mrpError || item.ayushpayPriceError || isIncomplete(item.mrp) || isIncomplete(item.ayushpayPrice))
    );
    if (hasInvalidLine) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const items = cart.map(item => ({
          mrp_per_unit: item.mrp,
          ayushpay_price_per_unit: item.ayushpayPrice,
          quantity: item.quantity,
          is_out_of_stock: item.outOfStock || false
        }));
        const response = await previewManualServiceOrderCashback(currentRole, orderId, items);
        if (!cancelled && response?.metadata?.api_status === true && response.msg?.cashback != null) {
          setCartCashback(response.msg.cashback);
        }
        if (!cancelled && response?.metadata?.api_status === true && response.msg?.delivery_charges != null && onDeliveryChargeChange) {
          onDeliveryChargeChange(response.msg.delivery_charges);
        }
      } catch (error) {
        if (!cancelled) console.error('Failed to preview cashback:', error);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [cart, currentRole, orderId, onDeliveryChargeChange]);

  // Add medicine to cart
  const addMedicineToCart = useCallback(async (medicine, showNotification) => {
    if (!orderId || !currentRole) {
      showNotification('Please select a service provider first', 'error');
      return;
    }

    const existingItem = cart.find(item => item.medicineId === medicine.id);
    const newQuantity = existingItem ? existingItem.quantity + 1 : 1;
    const mrp = medicine.price ?? medicine.mrp ?? 0;
    const ayushpayPrice = medicine.ayushpay_price ?? 0;

    if (ayushpayPrice > mrp) {
      showNotification('AyushPay Price cannot exceed MRP', 'error');
      return;
    }

    try {
      const itemData = {
        service_item_id: medicine.service_item_id ? medicine.service_item_id.trim() : null,
        service_item_name: medicine.name,
        service_item_unit: medicine.unit || 'unit',
        service_item_image: medicine.image || null,
        mrp_per_unit: mrp,
        ayushpay_price_per_unit: ayushpayPrice,
        partner_price_per_unit: medicine.price || 0,
        partner_discount_per_unit: 0,
        quantity: newQuantity,
        is_out_of_stock: false
      };

      const response = await addItemToManualServiceOrder(currentRole, orderId, itemData);
      
      if (response && response.metadata && response.metadata.api_status === true) {
        const newItem = {
          itemId: response.msg?.id,
          medicineId: medicine.id,
          name: medicine.name,
          // Carried from the search result so the pack label shows immediately,
          // without waiting for the next refreshCart().
          packSize: medicine.pack_size || '',
          quantity: newQuantity,
          mrp: mrp !== undefined && mrp !== null ? mrp : null,
          ayushpayPrice: ayushpayPrice !== undefined && ayushpayPrice !== null ? ayushpayPrice : null,
          outOfStock: false,
          image: medicine.image || null
        };

        if (existingItem) {
          setCart(cart.map(item =>
            item.medicineId === medicine.id ? newItem : item
          ));
        } else {
          setCart([...cart, newItem]);
        }
      } else {
        // Extract error message from response.msg if it's a string
        let errorMessage = 'Failed to add item';
        if (response?.msg && typeof response.msg === 'string') {
          errorMessage = response.msg;
        } else if (response?.msg?.error) {
          errorMessage = typeof response.msg.error === 'string' ? response.msg.error : 'Failed to add item';
        } else if (response?.body?.error) {
          errorMessage = response.body.error;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      let errorMessage = error.message || 'Failed to add medicine to cart';
      
      // If error is from API with JSON string, try to parse and extract msg
      if (errorMessage.includes('API Error') && errorMessage.includes('msg')) {
        try {
          // Try to extract JSON from error message
          const jsonMatch = errorMessage.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            const errorJson = JSON.parse(jsonMatch[0]);
            if (errorJson.msg && typeof errorJson.msg === 'string') {
              errorMessage = errorJson.msg;
            }
          }
        } catch (e) {
          // If parsing fails, use original message
        }
      }
      
      showNotification(errorMessage, 'error');
    }
  }, [cart, currentRole, orderId]);

  // Update cart item quantity
  const updateCartQuantity = useCallback((identifier, delta) => {
    const item = cart.find(i => (i.itemId && i.itemId === identifier) || (!i.itemId && i.medicineId === identifier));
    if (!item) return;
    const newQuantity = Math.max(1, item.quantity + delta);
    setCart(cart.map(cartItem =>
      (cartItem.itemId && cartItem.itemId === identifier) || (!cartItem.itemId && cartItem.medicineId === identifier)
        ? { ...cartItem, quantity: newQuantity }
        : cartItem
    ));
  }, [cart]);

  // Update cart item MRP
  const updateCartMRP = useCallback((identifier, newMRP) => {
    const num = parseFloat(newMRP);
    const mrpValue = newMRP === '' || newMRP === null || isNaN(num) ? null : num;
    setCart(cart.map(item => {
      if ((item.itemId && item.itemId === identifier) || (!item.itemId && item.medicineId === identifier)) {
        const mrpError = mrpValue != null && mrpValue < 0 ? 'MRP must be >= 0' : null;
        const ayushpayPriceError =
          item.ayushpayPrice != null && item.ayushpayPrice !== '' && item.ayushpayPrice < 0
            ? 'AyushPay Price must be >= 0'
            : item.ayushpayPrice != null && item.ayushpayPrice !== '' && mrpValue != null && !isNaN(mrpValue) && item.ayushpayPrice > mrpValue
              ? 'AyushPay Price cannot exceed MRP'
              : null;
        return { ...item, mrp: mrpValue, mrpError, ayushpayPriceError };
      }
      return item;
    }));
  }, [cart]);

  // Update cart item AyushPay price
  const updateCartAyushPayPrice = useCallback((identifier, newPrice) => {
    const num = parseFloat(newPrice);
    const priceValue = newPrice === '' || newPrice === null || isNaN(num) ? null : num;
    setCart(cart.map(item => {
      if ((item.itemId && item.itemId === identifier) || (!item.itemId && item.medicineId === identifier)) {
        const mrp = item.mrp != null && item.mrp !== '' ? parseFloat(item.mrp) : null;
        const ayushpayPriceError =
          priceValue != null && priceValue < 0
            ? 'AyushPay Price must be >= 0'
            : priceValue != null && !isNaN(priceValue) && mrp != null && !isNaN(mrp) && priceValue > mrp
              ? 'AyushPay Price cannot exceed MRP'
              : null;
        return { ...item, ayushpayPrice: priceValue, ayushpayPriceError };
      }
      return item;
    }));
  }, [cart]);

  // Toggle out of stock status
  const toggleOutOfStock = useCallback((identifier) => {
    setCart(cart.map(cartItem =>
      (cartItem.itemId && cartItem.itemId === identifier) || (!cartItem.itemId && cartItem.medicineId === identifier)
        ? { ...cartItem, outOfStock: !cartItem.outOfStock }
        : cartItem
    ));
  }, [cart]);

  // Remove from cart
  const removeFromCart = useCallback(async (identifier, showNotification) => {
    if (!orderId || !currentRole) return;
    
    const item = cart.find(i => (i.itemId && i.itemId === identifier) || (!i.itemId && i.medicineId === identifier));
    if (!item || !item.itemId) {
      setCart(cart.filter(i => !((i.itemId && i.itemId === identifier) || (!i.itemId && i.medicineId === identifier))));
      return;
    }

    try {
      const response = await removeItemFromManualServiceOrder(currentRole, orderId, item.itemId);
      
      if (response && response.metadata && response.metadata.api_status === true) {
        setCart(cart.filter(i => !((i.itemId && i.itemId === identifier) || (!i.itemId && i.medicineId === identifier))));
      } else {
        throw new Error(response?.msg?.error || response?.body?.error || 'Failed to remove item');
      }
    } catch (error) {
      showNotification(error.message || 'Failed to remove item', 'error');
    }
  }, [cart, currentRole, orderId]);

  // Batch update all cart items
  const batchUpdateCartItems = useCallback(async () => {
    if (!orderId || !currentRole) return;
    
    const validation = validateCartItems(cart);
    if (!validation.isValid) {
      throw new Error('Please fill in all required fields (MRP and AyushPay Price for all items)');
    }
    
    setCart(validation.updatedCart);
    
    const itemsToUpdate = cart.filter(item => item.itemId);
    if (itemsToUpdate.length === 0) return;
    
    try {
      const updatePromises = itemsToUpdate.map(item => {
        const updateData = {
          item_id: item.itemId,
          quantity: item.quantity,
          mrp_per_unit: item.mrp || 0,
          ayushpay_price_per_unit: item.ayushpayPrice || 0,
          is_out_of_stock: item.outOfStock || false
        };
        return updateItemInManualServiceOrder(currentRole, orderId, updateData);
      });
      
      const results = await Promise.allSettled(updatePromises);
      const failedUpdates = results.filter(r => 
        r.status === 'rejected' || 
        (r.status === 'fulfilled' && r.value?.metadata?.api_status !== true)
      );
      
      if (failedUpdates.length > 0) {
        console.warn('Some cart item updates failed:', failedUpdates);
      }
    } catch (error) {
      throw error;
    }
  }, [cart, currentRole, orderId]);

  // Refresh cart from API
  const refreshCart = useCallback(async () => {
    if (!currentRole || (!id && !walletTransactionId)) return;
    
    try {
      const response = await getPrefilledCart(currentRole, walletTransactionId ? null : id, walletTransactionId);
      if (response && response.metadata && response.metadata.api_status === true && response.msg) {
        const cartData = response.msg;
        if (cartData.has_cart && cartData.items && Array.isArray(cartData.items)) {
          const cartItems = cartData.items.map(mapCartItemFromAPI);
          setCart(cartItems);
          if (cartData.cashback !== undefined && cartData.cashback !== null) {
            setCartCashback(cartData.cashback);
          } else {
            setCartCashback(0);
          }
        }
      }
    } catch (error) {
      console.error('Failed to refresh cart:', error);
    }
  }, [currentRole, id, walletTransactionId]);

  return {
    cart,
    cartCashback,
    setCart,
    setCartCashback,
    addMedicineToCart,
    updateCartQuantity,
    updateCartMRP,
    updateCartAyushPayPrice,
    toggleOutOfStock,
    removeFromCart,
    batchUpdateCartItems,
    refreshCart
  };
};

